import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Masked } from "work/masked-fields";
import WorkAgreementRevisionLinks from "work/work-agreements/_views/view/work-agreement-revision-links";
import WorkAgreement from "work/work-agreements/work-agreement";
import { paneIsCompact } from "work/_views/work-pane-layout";

const WorkAgreementContainer = styled("div")(({ theme }) => ({
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(3),
}));
const Field = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(3),
  width: "50%",
}));
const FieldTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "1.2rem"
}));
const FieldValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  paddingLeft: theme.spacing(1),
}));

type DetailsTabProps = {
  workAgreement: WorkAgreement;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const { workAgreement } = props;

  const individualUserIds = useMemo(() => {
    const ids: Guid[] = [];
    if (Masked.isPresentNotMasked(workAgreement.clientTeam) && workAgreement.clientTeam.leader?.userId) {
      ids.push(workAgreement.clientTeam.leader.userId);
    }
    return ids;
  }, [workAgreement.clientTeam]);

  const individualsByUserIds = useSelector(getIndividualsByUserIds(individualUserIds));

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(individualUserIds.map(id => id.value)) },
    { skip: !individualUserIds.length }
  );

  const clientName = Masked.isMasked(workAgreement.clientTeam)
    ? "Insufficient Permissions to View Client"
    : (individualsByUserIds?.find((individual) =>
        !Masked.isMasked(workAgreement.clientTeam) && 
        individual.userId.isEqualTo(workAgreement.clientTeam?.leader?.userId ?? Guid.EMPTY),
      )?.fullName ?? "");

  return (
    <WorkAgreementContainer>
      <Field>
        <FieldTitle>Client Representative</FieldTitle>
        <FieldValue>{clientName}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>Description</FieldTitle>
        <FieldValue>{workAgreement.description?.value}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>Start Date</FieldTitle>
        <FieldValue>{workAgreement.startDate?.asFormattedString  ?? "None Specified"}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>End Date</FieldTitle>
        <FieldValue>{workAgreement.endDate?.asFormattedString ?? "None Specified"}</FieldValue>
      </Field>
      {(workAgreement.isReplaced ||
        workAgreement.revisesWorkAgreement ||
        workAgreement.hasOpenRevisionRequest) && (
        <Field>
          <FieldTitle>Revision</FieldTitle>
          <WorkAgreementRevisionLinks workAgreement={workAgreement} />
        </Field>
      )}
    </WorkAgreementContainer>
  );
}
